import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Typography, Box, LinearProgress, TextField, Button, InputAdornment, IconButton, Dialog, DialogContent, DialogTitle, Chip, Pagination, Select, MenuItem, FormControl, InputLabel, CircularProgress } from '@mui/material';
import Grid from "@mui/material/Grid2";
import { useParams, useLocation } from "react-router-dom";
import { getOperationsAdvanceReport } from 'src/shared/util/requestDataServer';
import { WrapMain } from 'src/shared/modules/wrap_main';
import Swal from "sweetalert2";
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import moment from 'moment';
import JSZip from 'jszip';
import { downloadLargeNumberOfFilesAndSendByEmail } from 'src/shared/util/requestDataServer';
import "./operationsStyles.scss";

interface PictureFile {
  fileSignedUrl: string;
  fileId: string;
  locationName: string;
  date: string;
  tags: string[];
}

interface LocationWithPictures {
  locationName: string;
  locationId: string;
  files: PictureFile[];
}

interface PicturesResponse {
  locationsWithPictures: LocationWithPictures[];
  locationsNamesDoesNotExist: string[];
}

interface ApiResponse {
  responseStatus: boolean;
  data: {
    status: boolean;
    data: PicturesResponse;
  };
}

// Componente SimpleImage para mostrar imágenes sin lazy loading
interface SimpleImageProps {
  src: string;
  alt: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  style?: React.CSSProperties;
}

const SimpleImage = React.memo(({ src, alt, className, onLoad, onError, style }: SimpleImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    onError?.();
  }, [onError]);

  return (
    <>
      {!isLoaded && !hasError && (
        <Box className="pictures-image-loading">
          <LinearProgress />
        </Box>
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          ...style,
          display: isLoaded && !hasError ? 'block' : 'none',
        }}
      />
    </>
  );
});

SimpleImage.displayName = 'SimpleImage';

// Función helper para formatear fechas (fuera del componente para evitar recreaciones)
const formatDateHelper = (dateString: string) => {
  return moment(dateString).format('DD-MM-YYYY');
};

// Componente optimizado para la tarjeta de imagen
interface ImageCardProps {
  file: PictureFile;
  locationName: string;
  onImageClick: (file: PictureFile, locationName: string) => void;
  onImageLoad: (fileId: string) => void;
  onImageError: (fileId: string) => void;
}

const ImageCard = React.memo(({ file, locationName, onImageClick, onImageLoad, onImageError }: ImageCardProps) => {
  return (
    <Box className="pictures-image-card">
      <Box
        className="pictures-image-wrapper"
        onClick={() => onImageClick(file, locationName)}
      >
        <SimpleImage
          src={file.fileSignedUrl}
          alt={`${locationName} - ${formatDateHelper(file.date)}`}
          className="pictures-image"
          onLoad={() => onImageLoad(file.fileId)}
          onError={() => onImageError(file.fileId)}
        />
      </Box>
      <Box className="pictures-card-footer">
        <Typography className="pictures-card-date">
          {formatDateHelper(file.date)}
        </Typography>
        {file.tags && file.tags.length > 0 && (
          <Box className="pictures-card-tags">
            {file.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                className="pictures-tag-chip"
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Solo re-renderizar si cambian las props relevantes
  return (
    prevProps.file.fileId === nextProps.file.fileId &&
    prevProps.file.fileSignedUrl === nextProps.file.fileSignedUrl &&
    prevProps.locationName === nextProps.locationName &&
    JSON.stringify(prevProps.file.tags) === JSON.stringify(nextProps.file.tags) &&
    prevProps.file.date === nextProps.file.date
  );
});

ImageCard.displayName = 'ImageCard';

export const PicturesAgileView = () => {
  const { startDate, endDate, type } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const locations = searchParams.get('locations') || '';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PicturesResponse | null>(null);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<{ url: string; locationName: string; date: string; tags: string[] } | null>(null);
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, boolean>>({});
  const [imageZoom, setImageZoom] = useState<number>(1);
  const [imagePosition, setImagePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);
  const [downloadProgress, setDownloadProgress] = useState<{
    currentDay: number;
    totalDays: number;
    currentFile: number;
    totalFiles: number;
    currentDayName: string;
    downloadedZips: number; // ZIPs descargados exitosamente
    downloadedFiles: number; // Archivos descargados del lote actual
  } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    getOperationsAdvanceReport(startDate!, endDate!, type!, "activitiesList", locations)
      .then((response: any) => {
        if (response?.responseStatus && response?.data?.data) {
          setData(response.data.data);
          // Inicializar estados de carga para todas las imágenes
          const initialLoadingStates: Record<string, boolean> = {};
          response.data.data.locationsWithPictures?.forEach((loc: LocationWithPictures) => {
            loc.files?.forEach((file: PictureFile) => {
              initialLoadingStates[file.fileId] = true;
            });
          });
          setImageLoadingStates(initialLoadingStates);
        } else {
          Swal.fire({
            title: "Error",
            html: response?.data?.message || "No se recibieron datos válidos",
            icon: "error",
            confirmButtonText: "Aceptar",
          });
        }
      })
      .catch(error => {
        console.error("Error al obtener datos:", error);
        Swal.fire({
          title: "Error",
          text: "Error al cargar las imágenes",
          icon: "error",
          confirmButtonText: "Aceptar",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [startDate, endDate, type, locations]);

  // Filtrar locations por nombre (tanto encontradas como no encontradas)
  const filteredLocations = useMemo(() => {
    if (!data) return [];
    if (!searchFilter.trim()) return data.locationsWithPictures;

    const filterLower = searchFilter.toLowerCase();
    return data.locationsWithPictures.filter(loc =>
      loc.locationName.toLowerCase().includes(filterLower)
    );
  }, [data, searchFilter]);

  // Filtrar locations no encontradas
  const filteredNotFoundLocations = useMemo(() => {
    if (!data) return [];
    if (!searchFilter.trim()) return data.locationsNamesDoesNotExist || [];

    const filterLower = searchFilter.toLowerCase();
    return (data.locationsNamesDoesNotExist || []).filter(locName =>
      locName.toLowerCase().includes(filterLower)
    );
  }, [data, searchFilter]);

  // Paginar locations filtradas
  const paginatedLocations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredLocations.slice(startIndex, endIndex);
  }, [filteredLocations, currentPage, itemsPerPage]);

  // Calcular total de páginas
  const totalPages = useMemo(() => {
    return Math.ceil(filteredLocations.length / itemsPerPage);
  }, [filteredLocations.length, itemsPerPage]);

  // Resetear página cuando cambia el filtro o itemsPerPage
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, itemsPerPage]);

  const handleImageLoad = (fileId: string) => {
    setImageLoadingStates(prev => ({ ...prev, [fileId]: false }));
  };

  const handleImageError = (fileId: string) => {
    setImageLoadingStates(prev => ({ ...prev, [fileId]: false }));
  };

  const handleImageClick = (file: PictureFile, locationName: string) => {
    setSelectedImage({
      url: file.fileSignedUrl,
      locationName,
      date: file.date,
      tags: file.tags,
    });
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleOpenInNewTab = () => {
    if (selectedImage) {
      window.open(selectedImage.url, '_blank');
    }
  };

  // Función para agrupar archivos por día
  const groupFilesByDay = (files: PictureFile[]): Array<{
    date: string;
    name: string;
    files: PictureFile[];
  }> => {
    const grouped: Record<string, PictureFile[]> = {};
    
    files.forEach(file => {
      const dayKey = moment(file.date).format('YYYY-MM-DD');
      if (!grouped[dayKey]) {
        grouped[dayKey] = [];
      }
      grouped[dayKey].push(file);
    });
    
    // Ordenar por fecha y retornar
    return Object.keys(grouped)
      .sort()
      .map(dayKey => ({
        date: dayKey,
        name: moment(dayKey).format('DD-MM-YYYY'),
        files: grouped[dayKey]
      }));
  };

  // Función para procesar archivos en lotes
  const processFilesInBatches = async (
    files: PictureFile[],
    batchSize: number,
    zip: JSZip,
    locationName?: string,
    onProgress?: (current: number, total: number, downloaded: number) => void
  ): Promise<{ success: number; errors: number }> => {
    let success = 0;
    let errors = 0;
    
    for (let i = 0; i < files.length; i += batchSize) {
      if (isCancelling) {
        throw new Error('Descarga cancelada por el usuario');
      }

      const batch = files.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async (file) => {
        try {
          const response = await fetch(file.fileSignedUrl, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-store',
            credentials: 'omit',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const blob = await response.blob();
          const buffer = await blob.arrayBuffer();
          
          // Usar locationName del parámetro si está disponible, sino usar el locationName del archivo
          const effectiveLocationName = locationName || file.locationName;
          let fileName = file.fileId;
          if (effectiveLocationName && !fileName.includes(effectiveLocationName)) {
            fileName = `${effectiveLocationName}_${fileName}`;
          }
          
          const extMatch = file.fileSignedUrl.match(/\.(\w+)(\?|$)/);
          const ext = extMatch ? extMatch[1] : "jpg";
          
          zip.file(`${fileName}.${ext}`, buffer);
          success++;
        } catch (error: any) {
          console.error(`Error al descargar ${file.fileSignedUrl}:`, error);
          errors++;
        }
      });

      await Promise.allSettled(batchPromises);
      
      if (onProgress) {
        onProgress(Math.min(i + batchSize, files.length), files.length, success);
      }

      // Pequeña pausa para evitar sobrecarga
      if (i + batchSize < files.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    return { success, errors };
  };

  // Función para descargar archivos divididos por días y lotes
  const downloadFilesByWeeks = async (files: PictureFile[], locationName?: string, batchSize: number = 500) => {
    setIsCancelling(false);
    const days = groupFilesByDay(files);
    
    // Dividir días según el batchSize seleccionado
    const processedDays: Array<{
      name: string;
      zipName: string;
      files: PictureFile[];
      dayIndex: number;
      lotIndex?: number;
      totalLots?: number;
    }> = [];
    
    days.forEach((day, dayIndex) => {
      const dayDateFormatted = moment(day.date).format('DD_MM_YYYY');
      
      if (day.files.length <= batchSize) {
        // Si el día tiene menos o igual que batchSize, crear un único ZIP
        processedDays.push({
          name: day.name,
          zipName: `${dayDateFormatted}_lote_unico`,
          files: day.files,
          dayIndex: dayIndex + 1
        });
      } else {
        // Si el día tiene más que batchSize, dividir en lotes
        const totalLots = Math.ceil(day.files.length / batchSize);
        for (let lotIndex = 0; lotIndex < totalLots; lotIndex++) {
          const lotStart = lotIndex * batchSize;
          const lotEnd = Math.min(lotStart + batchSize, day.files.length);
          processedDays.push({
            name: `${day.name} - Lote ${lotIndex + 1}`,
            zipName: `${dayDateFormatted}_lote_${lotIndex + 1}`,
            files: day.files.slice(lotStart, lotEnd),
            dayIndex: dayIndex + 1,
            lotIndex: lotIndex + 1,
            totalLots
          });
        }
      }
    });
    
    const totalDays = processedDays.length;
    
    setDownloadProgress({
      currentDay: 0,
      totalDays,
      currentFile: 0,
      totalFiles: files.length,
      currentDayName: '',
      downloadedZips: 0,
      downloadedFiles: 0
    });

    const results = {
      completed: 0,
      errors: 0,
      totalFiles: 0,
      errorFiles: 0
    };

    const startTime = Date.now();

    for (let dayIndex = 0; dayIndex < processedDays.length; dayIndex++) {
      if (isCancelling) {
        setDownloadProgress(null);
        setIsCancelling(false);
        return;
      }

      const day = processedDays[dayIndex];
      
      setDownloadProgress(prev => prev ? {
        ...prev,
        currentDay: dayIndex + 1,
        currentDayName: day.name,
        currentFile: 0,
        totalFiles: day.files.length,
        downloadedFiles: 0
      } : null);

      try {
        const zip = new JSZip();
        
        const stats = await processFilesInBatches(
          day.files,
          20, // Reducir a 20 archivos por lote para evitar problemas de memoria
          zip,
          locationName,
          (current, total, downloaded) => {
            setDownloadProgress(prev => prev ? {
              ...prev,
              currentFile: current,
              downloadedFiles: downloaded
            } : null);
          }
        );

        results.totalFiles += stats.success;
        results.errorFiles += stats.errors;

        if (stats.success > 0) {
          const zipBlob = await zip.generateAsync({ 
            type: "blob",
            streamFiles: true,
            compression: "DEFLATE",
            compressionOptions: {
              level: 1 // Compresión baja para usar menos memoria
            }
          });
          
          const zipName = locationName 
            ? `archivos_${locationName}_${day.zipName}.zip`
            : `archivos_${day.zipName}.zip`;

          // Descargar ZIP inmediatamente
          const link = document.createElement("a");
          link.href = URL.createObjectURL(zipBlob);
          link.download = zipName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);

          results.completed++;
          
          // Actualizar contador de ZIPs descargados
          setDownloadProgress(prev => prev ? {
            ...prev,
            downloadedZips: results.completed
          } : null);
          
          // Notificar día completado (solo si no es un lote, para no saturar)
          if (!day.lotIndex || day.lotIndex === 1) {
            Swal.fire({
              title: "Día descargado",
              html: `${day.name} descargado exitosamente<br/>${stats.success} archivos`,
              icon: "success",
              timer: 2000,
              showConfirmButton: false
            });
          }
        } else {
          results.errors++;
          Swal.fire({
            title: "Error en día",
            html: `No se pudieron descargar archivos de:<br/>${day.name}`,
            icon: "warning",
            timer: 3000,
            showConfirmButton: false
          });
        }

      } catch (error: any) {
        if (error.message === 'Descarga cancelada por el usuario') {
          setDownloadProgress(null);
          setIsCancelling(false);
          return;
        }
        console.error(`Error procesando día ${day.name}:`, error);
        results.errors++;
        Swal.fire({
          title: "Error",
          html: `Error al procesar:<br/>${day.name}`,
          icon: "error",
          timer: 3000,
          showConfirmButton: false
        });
      }
    }

    // Resumen final
    setDownloadProgress(null);
    setIsCancelling(false);
    const totalTime = ((Date.now() - startTime) / 1000 / 60).toFixed(1);
    
    Swal.fire({
      title: "Descarga completada",
      html: `
        <div style="text-align: left;">
          <p><strong>ZIPs completados:</strong> ${results.completed} de ${totalDays}</p>
          <p><strong>Archivos descargados:</strong> ${results.totalFiles}</p>
          <p><strong>Tiempo total:</strong> ${totalTime} minutos</p>
          ${results.errorFiles > 0 ? `<p><strong>Archivos con error:</strong> ${results.errorFiles}</p>` : ''}
          ${results.errors > 0 ? `<p><strong>ZIPs con error:</strong> ${results.errors}</p>` : ''}
        </div>
      `,
      icon: results.errors === 0 ? "success" : "warning",
      confirmButtonText: "Aceptar"
    });
  };

  const downloadFilesAsZip = async (files: PictureFile[], locationName?: string) => {
    // Si hay más de 500 archivos, usar descarga por semanas
    if (files.length > 50) {
      await downloadFilesByWeeks(files, locationName);
      return;
    }
    const zip = new JSZip();
    setLoading(true);

    try {
      const filePromises = files.map(async (file) => {
        try {
          // Usar fetch con mode: 'cors' y headers para evitar caché sin modificar la URL firmada
          // Esto fuerza el header Origin y evita usar respuestas cacheadas sin CORS
          // NO modificamos la URL porque las URLs firmadas de S3 tienen una firma que valida la URL completa
          const response = await fetch(file.fileSignedUrl, {
            method: 'GET',
            mode: 'cors', // Fuerza el header Origin, obliga a S3 a responder con headers CORS
            cache: 'no-store', // Evita usar caché HTTP (fuerza nueva petición)
            credentials: 'omit', // No envía cookies
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const blob = await response.blob();
          const buffer = await blob.arrayBuffer();
          
          // Construir nombre del archivo
          // Usar locationName del parámetro si está disponible, sino usar el locationName del archivo
          const effectiveLocationName = locationName || file.locationName;
          let fileName = file.fileId;
          if (effectiveLocationName && !fileName.includes(effectiveLocationName)) {
            fileName = `${effectiveLocationName}_${fileName}`;
          }
          
          // Obtener extensión de la URL
          const extMatch = file.fileSignedUrl.match(/\.(\w+)(\?|$)/);
          const ext = extMatch ? extMatch[1] : "jpg";
          
          zip.file(`${fileName}.${ext}`, buffer);
        } catch (error: any) {
          console.error(`Error al descargar ${file.fileSignedUrl}:`, error);
        }
      });

      await Promise.allSettled(filePromises);

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const startDateFormatted = startDate ? moment(startDate).format("YYYY-MM-DD") : "";
      const endDateFormatted = endDate ? moment(endDate).format("YYYY-MM-DD") : "";
      const zipName = locationName 
        ? `archivos_${locationName}_${startDateFormatted}_${endDateFormatted}.zip`
        : `archivos_${startDateFormatted}_${endDateFormatted}.zip`;

      // Descargar el archivo zip
      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = zipName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      Swal.fire({
        title: "Éxito",
        text: "Archivos descargados correctamente",
        icon: "success",
        confirmButtonText: "Aceptar",
      });
    } catch (error) {
      console.error("Error al descargar archivos:", error);
      Swal.fire({
        title: "Error",
        text: "Error al descargar los archivos",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLocation = async (location: LocationWithPictures) => {
    if (!location.files || location.files.length === 0) {
      Swal.fire({
        title: "Advertencia",
        text: "No hay archivos para descargar en esta locación",
        icon: "warning",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    // Mostrar modal para seleccionar tamaño de lote
    const { value: batchSize } = await Swal.fire({
      title: 'Configuración de descarga',
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 15px; color: #666;">
            <strong>⚠️ Advertencia:</strong> Este proceso puede demorar.
            Los archivos se descargarán por partes (ZIPs) organizados por día.
          </p>
          <p style="margin-bottom: 20px; color: #666;">
            Seleccione la cantidad de imágenes por ZIP:
          </p>
          <select id="batchSizeSelect" style="width: 100%; padding: 10px; font-size: 16px; border: 1px solid #ddd; border-radius: 4px;">
            <option value="50">50 imágenes por ZIP</option>
            <option value="100">100 imágenes por ZIP</option>
            <option value="200">200 imágenes por ZIP</option>
            <option value="500" selected>500 imágenes por ZIP (Recomendado)</option>
            <option value="700">700 imágenes por ZIP</option>
            <option value="1000">1000 imágenes por ZIP</option>
            <option value="2000">2000 imágenes por ZIP</option>
          </select>
          <p style="margin-top: 15px; font-size: 12px; color: #999;">
            <strong>Nota:</strong> Si un día tiene menos imágenes que el tamaño seleccionado, se creará un único ZIP con el nombre "DD_MM_YYYY_lote_unico".
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Iniciar descarga',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#d32f2f',
      didOpen: () => {
        const select = document.getElementById('batchSizeSelect') as HTMLSelectElement;
        if (select) {
          select.focus();
        }
      },
      preConfirm: () => {
        const select = document.getElementById('batchSizeSelect') as HTMLSelectElement;
        if (!select) {
          Swal.showValidationMessage('Por favor seleccione un tamaño de lote');
          return false;
        }
        return parseInt(select.value);
      }
    });

    if (!batchSize) {
      return; // Usuario canceló
    }

    // Siempre usar descarga por días con el tamaño de lote seleccionado
    await downloadFilesByWeeks(location.files, location.locationName, batchSize);
  };

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setImageZoom(prev => Math.max(0.5, Math.min(3, prev + delta)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && imageZoom > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleResetZoom = () => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };


  const handleDownloadAll = async () => {
    if (!data || !data.locationsWithPictures) {
      return;
    }

    // Obtener todos los archivos de todas las locations
    const allFiles: PictureFile[] = [];
    data.locationsWithPictures.forEach(location => {
      location.files.forEach(file => {
        allFiles.push({
          ...file,
          locationName: location.locationName
        });
      });
      //allFiles.push(...location.files);
    });
    

    // Mostrar modal para seleccionar tamaño de lote
    const { value: batchSize } = await Swal.fire({
      title: 'Configuración de descarga',
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 15px; color: #666;">
            <strong>⚠️ Advertencia:</strong> Este proceso es largo y puede demorar considerablemente.
            Los archivos se descargarán por partes (ZIPs) organizados por día.
          </p>
          <p style="margin-bottom: 20px; color: #666;">
            Seleccione la cantidad de imágenes por ZIP:
          </p>
          <select id="batchSizeSelect" style="width: 100%; padding: 10px; font-size: 16px; border: 1px solid #ddd; border-radius: 4px;">
            <option value="50">50 imágenes por ZIP</option>
            <option value="100">100 imágenes por ZIP</option>
            <option value="200">200 imágenes por ZIP</option>
            <option value="500" selected>500 imágenes por ZIP (Recomendado)</option>
            <option value="700">700 imágenes por ZIP</option>
            <option value="1000">1000 imágenes por ZIP</option>
            <option value="2000">2000 imágenes por ZIP</option>
          </select>
          <p style="margin-top: 15px; font-size: 12px; color: #999;">
            <strong>Nota:</strong> Si un día tiene menos imágenes que el tamaño seleccionado, se creará un único ZIP con el nombre "DD_MM_YYYY_lote_unico".
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Iniciar descarga',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#d32f2f',
      didOpen: () => {
        const select = document.getElementById('batchSizeSelect') as HTMLSelectElement;
        if (select) {
          select.focus();
        }
      },
      preConfirm: () => {
        const select = document.getElementById('batchSizeSelect') as HTMLSelectElement;
        if (!select) {
          Swal.showValidationMessage('Por favor seleccione un tamaño de lote');
          return false;
        }
        return parseInt(select.value);
      }
    });

    if (!batchSize) {
      return; // Usuario canceló
    }

    const totalFiles = allFiles.length;

    if (totalFiles === 0) {
      Swal.fire({
        title: "Advertencia",
        text: "No hay archivos para descargar",
        icon: "warning",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    await downloadFilesAsZip(allFiles);
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format('DD-MM-YYYY');
  };

  return (
    <WrapMain>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {loading && (
        <>
          <Box className="modal_analyst_loading_background" />
          <Box className="modal__analyst_task_loading">
            <LinearProgress />
            Consultando....
          </Box>
        </>
      )}

      {/* Modal de progreso de descarga */}
      {downloadProgress && (
        <Dialog open={true} maxWidth="sm" fullWidth onClose={() => {}}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Descargando archivos por períodos
            <IconButton
              onClick={() => {
                setIsCancelling(true);
                Swal.fire({
                  title: "¿Cancelar descarga?",
                  text: "La descarga será cancelada. Los archivos ya descargados se mantienen.",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Sí, cancelar",
                  cancelButtonText: "Continuar"
                }).then((result) => {
                  if (result.isConfirmed) {
                    setDownloadProgress(null);
                    setIsCancelling(false);
                  } else {
                    setIsCancelling(false);
                  }
                });
              }}
              size="small"
              sx={{ color: 'error.main' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom fontWeight="bold">
                Procesando día {downloadProgress.currentDay} de {downloadProgress.totalDays}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {downloadProgress.currentDayName}
              </Typography>
              
              {/* Barra de progreso general - ZIPs descargados vs faltantes */}
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  ZIPs descargados: {downloadProgress.downloadedZips} de {downloadProgress.totalDays} (Faltantes: {downloadProgress.totalDays - downloadProgress.downloadedZips})
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(downloadProgress.downloadedZips / downloadProgress.totalDays) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {Math.round((downloadProgress.downloadedZips / downloadProgress.totalDays) * 100)}% completado
                </Typography>
              </Box>

              {/* Barra de progreso del lote actual */}
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Archivos del ZIP actual: {downloadProgress.currentFile} de {downloadProgress.totalFiles}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={downloadProgress.totalFiles > 0 ? (downloadProgress.currentFile / downloadProgress.totalFiles) * 100 : 0}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {Math.round((downloadProgress.currentFile / downloadProgress.totalFiles) * 100)}% completado
                </Typography>
              </Box>

              {/* Contador de imágenes descargadas */}
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: '#1976d2' }} />
                <Typography variant="body2" fontWeight="bold" color="text.primary">
                  Imágenes descargadas del ZIP actual: {downloadProgress.downloadedFiles} de {downloadProgress.totalFiles}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      <Box className="pictures-agile-view-container">
        {/* Header fijo con búsqueda y descarga */}
        <Box className="pictures-header-fixed">
          <Grid className="pictures-header">
            <TextField
              className="pictures-search-input"
              placeholder="Buscar por nombre de pozo..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchFilter && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchFilter('')}
                      title="Limpiar búsqueda"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleDownloadAll}
              className="pictures-download-btn"
            >
              Descargar Completo
            </Button>
          </Grid>

          {/* Lista de locations que no existen */}
          {filteredNotFoundLocations.length > 0 && (
            <Grid className="pictures-not-found-section">
              <Typography className="pictures-not-found-title">
                Locaciones no encontradas:
              </Typography>
              {filteredNotFoundLocations.map((locationName, index) => (
                <Chip
                  key={index}
                  label={locationName}
                  color="error"
                  className="pictures-not-found-chip"
                />
              ))}
            </Grid>
          )}
        </Box>

        {/* Área scrolleable con los pozos */}
        <Box className="pictures-scrollable-content">
          {filteredLocations.length === 0 && !loading && (
            <Box className="pictures-empty-state">
              <Typography variant="h6" color="text.secondary">
                {searchFilter ? 'No se encontraron locaciones con el filtro aplicado' : 'No hay imágenes disponibles'}
              </Typography>
            </Box>
          )}

          {paginatedLocations.map((location) => {
            return (
              <Box
                key={location.locationId}
                sx={{
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  backgroundColor: 'white',
                }}
              >
                {/* Header de la location */}
                <Box
                  sx={{
                    height: '50px',
                    backgroundColor: '#468dec',
                    color: 'white',
                    padding: '5px 20px',
                    borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': {
                      backgroundColor: '#3a7bd5',
                    },
                  }}
                >
                  <Typography 
                    className="pictures-location-name" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '18px',
                      color: 'white'
                    }}
                  >
                    {location.locationName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ marginRight: '8px', color: 'white' }}>
                      {location.files.length} {location.files.length === 1 ? 'imagen' : 'imágenes'}
                    </Typography>
                    <IconButton
                      className="pictures-location-download-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadLocation(location);
                      }}
                      title="Descargar todas las imágenes de esta locación"
                      size="small"
                      sx={{
                        color: 'white !important',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        },
                      }}
                    >
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                {/* Contenido de imágenes - siempre visible */}
                  <Grid className="pictures-cards-container">
                    {location.files.map((file) => (
                      <ImageCard
                        key={file.fileId}
                        file={file}
                        locationName={location.locationName}
                        onImageClick={handleImageClick}
                        onImageLoad={handleImageLoad}
                        onImageError={handleImageError}
                      />
                    ))}
                  </Grid>
              </Box>
            );
          })}
        </Box>

        {/* Paginación fija en la parte inferior */}
        {filteredLocations.length > 0 && (
          <Box className="pictures-pagination-fixed">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Typography variant="body2" color="text.secondary">
                  Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredLocations.length)} de {filteredLocations.length} pozos 
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Pozos por página</InputLabel>
                  <Select
                    value={itemsPerPage}
                    label="Pozos por página"
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
        )}
      </Box>

      {/* Modal para ver imagen en grande */}
      {selectedImage && (
        <Dialog
          open={!!selectedImage}
          onClose={handleCloseModal}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'rgb(213, 229, 245)',
            }
          }}
        >
          <DialogTitle>
            <Box className="pictures-modal-header">
              <Typography variant="h6">
                {selectedImage.locationName} - {formatDate(selectedImage.date)}
              </Typography>
              <IconButton onClick={handleCloseModal}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box className="pictures-modal-content-wrapper">
              {/* Botón fijo para abrir en nueva pestaña */}
              <IconButton
                className="pictures-modal-open-btn"
                onClick={handleOpenInNewTab}
                title="Abrir en nueva pestaña"
              >
                <OpenInNewIcon />
              </IconButton>

              <Box
                className="pictures-modal-content"
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                sx={{ cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
              >
                <Box
                  className="pictures-modal-image-container"
                  sx={{
                    transform: `scale(${imageZoom}) translate(${imagePosition.x / imageZoom}px, ${imagePosition.y / imageZoom}px)`,
                    transformOrigin: 'center center',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                  }}
                >
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.locationName}
                    className="pictures-modal-image"
                    draggable={false}
                  />
                </Box>
                {selectedImage.tags && selectedImage.tags.length > 0 && (
                  <Box className="pictures-modal-tags">
                    {selectedImage.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        className="pictures-tag-chip"
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Botones de zoom en la parte inferior */}
              <Box className="pictures-modal-zoom-controls">
                <IconButton onClick={handleZoomOut} title="Alejar" className="pictures-zoom-btn">
                  <ZoomOutIcon />
                </IconButton>
                <Typography variant="body2" className="pictures-zoom-percentage">
                  {Math.round(imageZoom * 100)}%
                </Typography>
                <IconButton onClick={handleZoomIn} title="Acercar" className="pictures-zoom-btn">
                  <ZoomInIcon />
                </IconButton>
                {imageZoom !== 1 && (
                  <IconButton onClick={handleResetZoom} title="Restablecer zoom" className="pictures-zoom-reset-btn">
                    <Typography variant="caption">Reset</Typography>
                  </IconButton>
                )}
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
      </Box>
    </WrapMain>
  );
};
